<!-- Copyright: Seán I. O'Donoghue -->
<!-- A film shown for a limited time (Seán, 2026-11-05): Martin Dohrn's The Birds, a keynote of
     VIZBI 2026, at /Videos/The_Birds (films.js). Laid out as a video's page (Bespoke.vue): its
     preview's thumbnail, locked as a locked video for anyone not in its audience; for its
     audience, a countdown until it opens, then the player.
     The fullstack decides who and when (api/v1/videos/film/<key>); an admin switches the
     audience here: VIZBI Online's registrants (the default) or all alumni. -->
<template>
  <figure v-if="film" :class="{hasAccessStack: isLocked}">
    <div v-if="isAdmin && status" class="adminControl">
      <SegmentedControl
        class="audienceSwitch"
        :model-value="AUDIENCE_LABELS[status.audience]"
        :options="Object.values(AUDIENCE_LABELS)"
        label="Who can watch"
        @update:model-value="saveAudience" />
      <span class="adminNote">Admins only: who can watch the film</span>
    </div>
    <!-- signed out, or not in its audience: a locked video's state (Bespoke.vue), the countdown
         only for its audience (Seán, 2026-11-05) -->
    <div v-if="isLocked" class="access-stack">
      <VideoAccessInfoButton
        label="How do I unlock this video?"
        is-prominent
        @click="router.push({name: 'Videos_Unlock'})" />
      <div class="access-inline-buttons">
        <VideoAccessActionButton
          :href="registrationUrl('workshop')"
          label="Purchase access"
          @click="redirectToRegistration('workshop')" />
        <VideoAccessActionButton
          v-show="status.access === 'login'"
          href="#sign-in"
          label="Sign in"
          @click="signIn" />
      </div>
    </div>
    <div id="wrapper">
      <vue-vimeo-player
        v-if="status?.access === 'play'"
        class="child"
        :video-url="status.videoUrl"
        :options="PLAYER_OPTIONS" />
      <div
        v-else class="child thumbnail-container" :class="{locked: isLocked}"
        @click="onThumbnailClick">
        <img v-if="thumbnail" :src="thumbnail" alt="" />
        <div v-if="isLocked" class="lock-overlay">
          <span class="lock-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
            </svg>
          </span>
        </div>
        <!-- the countdown in a line at the top, read left to right, and the preview's button at
             the bottom, as wide as the line (Seán, 2026-11-05) -->
        <div v-else-if="status" class="overlay">
          <p v-if="status.access === 'soon'" class="overlayLabel">
            Available in <span class="remaining">{{ remainingText }}</span>
          </p>
          <p v-else-if="status.access === 'pending'" class="overlayLabel">Coming soon</p>
          <p v-else-if="status.access === 'closed'" class="overlayLabel">No longer available</p>
          <a v-if="film.previewUrl" class="previewButton" :href="film.previewUrl">
            Watch Preview on Martin's Site
          </a>
        </div>
      </div>
    </div>
    <figcaption>
      <h4 class="videoTitle">
        <span class="titleSpeaker">{{ speaker }}:&#32;</span>
        <span class="titleTalk">{{ talk }}</span>
      </h4>
      <p v-if="status" class="filmFacts">
        Film, {{ film.length }}. Available {{ windowText }}
        (<TimeZoneInline />).
      </p>
      <VideoDescription :description="description" :video-name="film.name" :year="year" />
    </figcaption>
  </figure>
</template>

<script setup>
import {computed, inject, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {DateTime} from "luxon";
import {vueVimeoPlayer as VueVimeoPlayer} from "vue-vimeo-player";
import TimeZoneInline from "@/components/TimeZoneInline.vue";
import SegmentedControl from "@/components/App/SegmentedControl.vue";
import VideoDescription from "@/components/Video/Description.vue";
import VideoAccessActionButton from "@/components/Video/VideoAccessActionButton.vue";
import VideoAccessInfoButton from "@/components/Video/VideoAccessInfoButton.vue";
import {getFilm} from "@/components/Video/films";
import {getThumbnailUrl, getPictures} from "@/components/Video/videoCards";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import useRoles from "@/composables/useRoles";
import useUserSafe from "@/composables/useUserSafe";
import {getPreferredTimeZone} from "@/composables/selectedTimeZones";
import {requestSignIn} from "@/composables/useSignInRequest";
import {registrationUrl, redirectToRegistration} from "@/composables/useRedirectToRegistration";
import {setPageMeta} from "@/composables/usePageMeta";
import {getServerNow} from "@/composables/serverClock";

const props = defineProps({options: {type: Object, default: () => ({})}});

const AUDIENCE_LABELS = {online: "VIZBI Online", alumni: "All alumni"};
const PLAYER_OPTIONS = {responsive: true, title: false, portrait: false, byline: false};
const ZONE = "Europe/London";

const getUkTime = (iso) => DateTime.fromISO(iso).setZone(ZONE);
const getAudienceKey = (label) => {
  return Object.keys(AUDIENCE_LABELS).find((key) => AUDIENCE_LABELS[key] === label);
};

const route = useRoute();
const router = useRouter();
const {isSignedIn} = inject("authentication");
const {isAdmin} = useRoles();
const status = ref(null);
const film = computed(() => getFilm(route.params.vimeoHash));
const video = computed(() => {
  return props.options.showcases?.[film.value?.showcase]?.videos?.[route.params.vimeoHash];
});
const [speaker, talk] = (getFilm(route.params.vimeoHash)?.name || ":").split(": ");
const year = computed(() => film.value?.showcase.match(/\d+/)[0]);
const description = computed(() => video.value?.description || film.value?.intro || "");
const thumbnail = computed(() => {
  const pictures = getPictures(props.options.showcases || {}, film.value?.showcase,
    route.params.vimeoHash);
  return pictures && getThumbnailUrl(pictures, pictures.sizes[3]);
});
const opensAt = computed(() => getUkTime(status.value?.opensAt));
// the time left, in words: "11 days, 13 hours, 45 minutes, 19 seconds" (the server's clock)
const UNITS = ["days", "hours", "minutes", "seconds"];
const now = ref(getServerNow());
const getUnitText = (count, unit) => `${count} ${count === 1 ? unit.slice(0, -1) : unit}`;
const remainingText = computed(() => {
  const left = opensAt.value.diff(now.value, UNITS).toObject();
  return UNITS.map((unit) => getUnitText(Math.max(0, Math.floor(left[unit])), unit)).join(", ");
});
// in the viewer's chosen time zone, as /2026's dates (TimeZoneInline.vue)
const {user} = useUserSafe();
const viewerZone = computed(() => getPreferredTimeZone(user.value));
const windowText = computed(() => {
  const format = (iso) => {
    return DateTime.fromISO(iso).setZone(viewerZone.value).toFormat("d MMMM, HH:mm");
  };
  return `${format(status.value.opensAt)} – ${format(status.value.closesAt)}`;
});
const LOCKED = ["login", "register"];
const isLocked = computed(() => LOCKED.includes(status.value?.access));

async function fetchStatus() {
  const response = await authFetch(`/api/v1/videos/film/${film.value.key}`);
  status.value = await readJson(response, null);
}

async function saveAudience(label) {
  const audience = getAudienceKey(label);
  const options = {method: "POST", headers: {"Content-Type": "application/json"},
    body: JSON.stringify({audience})};
  const response = await authFetch(`/api/v1/admin/films/${film.value.key}/audience`, options);
  if (response.ok) await fetchStatus();
}

function signIn() {
  requestSignIn(router, "lockedWorkshop", fetchStatus);
}

function onThumbnailClick() {
  if (status.value?.access === "login") return signIn();
  if (status.value?.access === "register") redirectToRegistration("workshop");
}

function tick() {
  now.value = getServerNow();
  if (status.value?.access === "soon" && now.value >= opensAt.value) fetchStatus();
}
let ticker = null;
onMounted(() => {
  fetchStatus();
  ticker = setInterval(tick, 1000);
});
onBeforeUnmount(() => clearInterval(ticker));
watch(isSignedIn, fetchStatus);
watch(film, (value) => {
  if (!value) return;
  setPageMeta(route.path, {title: `${value.name} - VIZBI ${year.value}`, type: "video.other"});
}, {immediate: true});
</script>

<style scoped>
figure {
  display: flex;
  flex-direction: column;
  margin: 20px 0 0 0;
  width: 100% !important;
}
.adminControl {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
/* each label whole: the control's equal widths cut "VIZBI Online" short beside the note */
.audienceSwitch {
  flex: none;
}
.audienceSwitch :deep(button) {
  flex: 1 0 auto;
}
.adminNote {
  font-size: 0.85rem;
  color: color-mix(in srgb, var(--text-color) 60%, transparent);
}
/* as Bespoke.vue's: 16:9, the player or the thumbnail filling it */
#wrapper {
  position: relative;
  padding-bottom: calc(100% / (640 / 360));
}
#wrapper .child {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.thumbnail-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
figure.hasAccessStack {
  margin-top: 0;
}
/* as Bespoke.vue's: the unlock bar and its buttons over the video, the lock on it */
.access-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  width: 100%;
  box-sizing: border-box;
}
.access-inline-buttons {
  display: flex;
  gap: 8px;
}
.access-inline-buttons > * {
  flex: 1;
}
.thumbnail-container.locked {
  cursor: pointer;
}
.lock-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.lock-icon {
  width: 64px;
  height: 64px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lock-icon svg {
  width: 32px;
  height: 32px;
}
/* over the darkened thumbnail, its audience only: the line at the top, the button at the bottom,
   one column as wide as the wider of the two */
.overlay {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: auto 1fr auto;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  text-align: center;
}
.overlayLabel {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}
.remaining {
  font-variant-numeric: tabular-nums; /* the line keeps its width as the seconds tick */
}
.previewButton {
  grid-row: 3;
  display: block;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  background: var(--brand-color);
  color: #000;
  font-size: 1.2rem;
  font-weight: 700;
  text-decoration: none;
}
.previewButton:hover {
  background: var(--brand-hover-color, var(--brand-color));
  color: #fff;
}
.videoTitle {
  font-size: 1.625rem;
  line-height: 1.25;
}
.darkMode .videoTitle {
  color: #fff;
}
.titleSpeaker {
  font-weight: 700;
  text-transform: uppercase;
}
.titleTalk {
  font-weight: 600;
}
.narrow .videoTitle {
  font-size: clamp(1.1rem, 5.4dvw, 1.625rem);
}
.filmFacts {
  margin: 0 0 0.5rem;
  color: color-mix(in srgb, var(--text-color) 70%, transparent);
}
</style>
